/**
 * Read-only consent-cookie state, split out of lib/consent.ts so modules that
 * only need to *read* the decision (lib/posthog-events.ts, the analytics
 * component) don't import the write path — which pulls in the analytics SDK shims
 * and would otherwise form an import cycle (consent -> posthog-events -> consent).
 *
 * This module is a leaf: it imports nothing of ours.
 */

export interface ConsentChoice {
  readonly analytics: boolean;
  readonly marketing: boolean;
}

export const CONSENT_COOKIE = "sq_consent";

// Bumped to 2 when PostHog joined the analytics category. Visitors who consented
// under v1 chose before PostHog (and its cookies) existed, so they must re-decide
// — a version bump invalidates stored consent and re-prompts everyone.
export const CONSENT_VERSION = 2;

const isStoredConsent = (
  value: unknown,
): value is ConsentChoice & { readonly v: number } =>
  typeof value === "object" &&
  value !== null &&
  "v" in value &&
  "analytics" in value &&
  "marketing" in value &&
  value.v === CONSENT_VERSION &&
  typeof value.analytics === "boolean" &&
  typeof value.marketing === "boolean";

/**
 * Parse a consent-cookie VALUE (already URI-decoded — both document.cookie
 * readers after decodeURIComponent and Next's server cookies() qualify) into
 * the stored decision, or null if absent/invalid/stale-versioned. Pure, so
 * server code (e.g. the signup callback gating attribution persistence on
 * marketing consent) shares the exact same validation as the client.
 */
export function parseStoredConsent(raw: string | null | undefined): ConsentChoice | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isStoredConsent(parsed)
      ? { analytics: parsed.analytics, marketing: parsed.marketing }
      : null;
  } catch {
    return null;
  }
}

/** The stored consent decision, or null if absent/invalid. SSR-safe (no cookie). */
export function readStoredConsent(): ConsentChoice | null {
  if (typeof document === "undefined") return null;
  const match = new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]*)`).exec(document.cookie);
  if (!match) return null;
  try {
    return parseStoredConsent(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}
